'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

import { css } from 'styled-system/css';

import { checkIntersectionObserverHealth } from '@/hooks/use-intersection-observer-health';

type Props = { className: string; videoUrl: string; children?: ReactNode };

const videoClass = css({ position: 'absolute', inset: 0, w: '100%', h: '100%', objectFit: 'cover' });

/**
 * SiemensAnniversary's media slot as an island: the <video> mounts 200px before the viewport (or
 * eagerly when IntersectionObserver never delivers), the SSR'd poster (children) is the stand-in.
 * Reduced motion: no autoplay, controls instead (was Chakra's usePrefersReducedMotion — live).
 */
export const SiemensVideo = ({ className, videoUrl, children }: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!hostRef.current) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setShouldLoad(true), {
      rootMargin: '200px',
    });
    io.observe(hostRef.current);
    let isMounted = true;
    checkIntersectionObserverHealth().then((healthy) => {
      if (isMounted && !healthy) setShouldLoad(true);
    });
    return () => {
      isMounted = false;
      io.disconnect();
    };
  }, []);

  return (
    <div ref={hostRef} className={className}>
      {children}
      {shouldLoad && (
        <video
          src={videoUrl}
          autoPlay={!reducedMotion}
          controls={reducedMotion}
          muted
          loop
          playsInline
          preload="none"
          className={videoClass}
        />
      )}
    </div>
  );
};
